import { PlusIcon, UserGroupIcon, UserIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { BASE } from './_Layout'
import { pct } from './analyticsSections'
import InfoTip from './InfoTip'
import {
    capacityFor,
    caseloadFor,
    MOCK_FAMILY_SUMMARY,
    MOCK_FORMER_MEMBERS,
    MOCK_MEMBERS,
    MockMember,
    orgCaseloadUtilization,
    TEAM_METRICS,
} from './mockData'
import { useProviderSession } from './previewSession'

export const TEAM_ROLE_LABELS: Record<string, string> = {
    admin: 'Admin',
    coordinator: 'Coordinator',
    viewer: 'Viewer',
}

export function formatSignIn(value: string | null): string {
    if (!value) return 'Never'
    const d = new Date(value)
    if (isNaN(d.valueOf())) return 'Never'
    const days = Math.floor((Date.now() - d.valueOf()) / (24 * 60 * 60 * 1000))
    if (days <= 0) return 'Today'
    if (days === 1) return 'Yesterday'
    if (days < 14) return `${days} days ago`
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function ProviderTeam() {
    const session = useProviderSession()
    const isAdmin = session?.role === 'admin'

    const [members, setMembers] = useState<MockMember[]>(MOCK_MEMBERS)
    const former = MOCK_FORMER_MEMBERS
    const [showAdd, setShowAdd] = useState(false)

    if (session && !isAdmin) {
        return (
            <div className='max-w-6xl mx-auto px-4 py-12 text-center text-sm text-neutral-500'>
                Team Members is available to organization admins only.
            </div>
        )
    }

    const list = members
    const util = orgCaseloadUtilization()
    const actionsWoW =
        TEAM_METRICS.actionsPrev7d > 0
            ? (TEAM_METRICS.actions7d - TEAM_METRICS.actionsPrev7d) / TEAM_METRICS.actionsPrev7d
            : 0
    const atRisk = MOCK_FAMILY_SUMMARY.atRisk
    const atRiskDelta = atRisk - TEAM_METRICS.atRiskClientsPrev

    return (
        <div className='max-w-6xl mx-auto px-4 sm:px-6 py-8'>
            <div className='flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6'>
                <div>
                    <h1 className='text-2xl sm:text-3xl font-bold text-neutral-900'>Team Members</h1>
                    <p className='text-sm text-neutral-600 mt-1'>
                        Your organization&apos;s staff and how actively they use the portal.
                    </p>
                </div>
                {isAdmin && (
                    <button
                        onClick={() => setShowAdd(true)}
                        className='inline-flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg transition-colors'
                    >
                        <PlusIcon className='w-4 h-4' />
                        Add team member
                    </button>
                )}
            </div>

            {/* Aggregate tiles */}
            <div className='grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6'>
                <Stat label='Team members' value={String(list.length)} sub='in your organization' />
                <Stat
                    label='Caseload utilization rate'
                    value={pct(util.rate)}
                    sub={`${util.assigned} of ${util.capacity} caseload slots filled`}
                    info='Clients currently assigned to a coordinator divided by total coordinator capacity across the organization.'
                />
                <Stat
                    label='Total actions (last 7 days)'
                    value={String(TEAM_METRICS.actions7d)}
                    sub={<WoW value={actionsWoW} goodWhenUp />}
                    info='Portal actions (roster views, client views, notes, homework, invites, and similar) recorded across the whole team in the last 7 days, with the change versus the previous week.'
                />
                <Stat
                    label='Total at-risk clients'
                    value={String(atRisk)}
                    sub={<WoW count={atRiskDelta} unit='client' goodWhenUp={false} />}
                    info='Clients showing disengagement (no meaningful activity in 30+ days, stalled roadmap, or incomplete onboarding), with the change versus the previous week.'
                />
            </div>

            <div className='bg-white border border-neutral-200 rounded-xl overflow-hidden'>
                <div className='overflow-x-auto'>
                    <table className='w-full text-sm'>
                        <thead className='bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wider'>
                            <tr>
                                <th className='px-4 py-3 text-left font-medium'>Member</th>
                                <th className='px-4 py-3 text-left font-medium'>Role</th>
                                <th className='px-4 py-3 text-left font-medium'>
                                    Caseload
                                    <InfoTip text='Clients currently assigned to this coordinator, shown against their target capacity. Admins see every client; viewers are read-only and are not assigned a caseload.' />
                                </th>
                                <th className='px-4 py-3 text-left font-medium'>Last sign-in</th>
                                <th className='px-4 py-3 text-left font-medium'>
                                    Actions (30d)
                                    <InfoTip text='Portal actions this member recorded in the last 30 days: roster and client views, notes, homework, invites, and similar.' />
                                </th>
                                {isAdmin && <th className='px-4 py-3 text-right font-medium'>&nbsp;</th>}
                            </tr>
                        </thead>
                        <tbody className='divide-y divide-neutral-100'>
                            {list.map((m) => (
                                <tr key={m._id} className='hover:bg-neutral-50/50'>
                                    <td className='px-4 py-3'>
                                        <div className='flex items-center gap-3'>
                                            <div className='w-9 h-9 bg-brand-100 rounded-full flex items-center justify-center flex-shrink-0'>
                                                <UserIcon className='w-4 h-4 text-brand-700' />
                                            </div>
                                            <div className='min-w-0'>
                                                <div className='font-medium text-neutral-900 truncate'>
                                                    {m.name || m.email}
                                                </div>
                                                <div className='text-xs text-neutral-500 truncate'>{m.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className='px-4 py-3'>
                                        <span className='text-xs font-medium text-brand-700 bg-brand-50 border border-brand-100 rounded-full px-2.5 py-1'>
                                            {TEAM_ROLE_LABELS[m.role] ?? m.role}
                                        </span>
                                    </td>
                                    <td className='px-4 py-3 text-neutral-600'>
                                        {m.role === 'coordinator'
                                            ? `${caseloadFor(m.userId)} clients / ${capacityFor(m.userId)} capacity`
                                            : m.role === 'admin'
                                              ? 'All clients'
                                              : '—'}
                                    </td>
                                    <td className='px-4 py-3 text-neutral-600'>{formatSignIn(m.lastSignInAt)}</td>
                                    <td className='px-4 py-3 text-neutral-600'>{m.actions30d}</td>
                                    {isAdmin && (
                                        <td className='px-4 py-3 text-right'>
                                            <Link
                                                to={`${BASE}/team/${m._id}`}
                                                className='inline-flex items-center px-3 py-1.5 text-xs font-medium text-brand-700 bg-brand-50 hover:bg-brand-100 border border-brand-200 rounded-lg transition-colors'
                                            >
                                                View
                                            </Link>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {list.length === 0 && (
                    <div className='py-12 text-center text-sm text-neutral-500'>
                        <UserGroupIcon className='w-8 h-8 mx-auto mb-2 text-neutral-300' />
                        No team members yet.
                    </div>
                )}
            </div>

            <div className='mt-4 p-3 bg-neutral-50 border border-neutral-200 rounded-lg text-xs text-neutral-700'>
                <strong>Roles:</strong> Admin can manage licenses, team members, and see all clients. Coordinator
                manages licenses and works hands-on with their assigned clients. Viewer sees all clients and
                analytics but is read-only.
            </div>

            {/* Former members — preserved for auditing after removal */}
            {isAdmin && former.length > 0 && (
                <div className='mt-8'>
                    <h2 className='text-lg font-semibold text-neutral-900 mb-1'>Former members</h2>
                    <p className='text-sm text-neutral-600 mb-4'>
                        Removed from your organization. Their action history is preserved for auditing.
                    </p>
                    <div className='bg-white border border-neutral-200 rounded-xl overflow-hidden'>
                        <div className='overflow-x-auto'>
                            <table className='w-full text-sm'>
                                <thead className='bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wider'>
                                    <tr>
                                        <th className='px-4 py-3 text-left font-medium'>Member</th>
                                        <th className='px-4 py-3 text-left font-medium'>Last role</th>
                                        <th className='px-4 py-3 text-left font-medium'>Removed</th>
                                        <th className='px-4 py-3 text-right font-medium'>&nbsp;</th>
                                    </tr>
                                </thead>
                                <tbody className='divide-y divide-neutral-100'>
                                    {former.map((m) => (
                                        <tr key={m.userId} className='hover:bg-neutral-50/50'>
                                            <td className='px-4 py-3'>
                                                <div className='flex items-center gap-3'>
                                                    <div className='w-9 h-9 bg-neutral-100 rounded-full flex items-center justify-center flex-shrink-0'>
                                                        <UserIcon className='w-4 h-4 text-neutral-500' />
                                                    </div>
                                                    <div className='min-w-0'>
                                                        <div className='font-medium text-neutral-900 truncate'>
                                                            {m.name || m.email}
                                                        </div>
                                                        <div className='text-xs text-neutral-500 truncate'>{m.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className='px-4 py-3'>
                                                <span className='text-xs font-medium text-neutral-600 bg-neutral-100 border border-neutral-200 rounded-full px-2.5 py-1'>
                                                    {m.lastRole ? TEAM_ROLE_LABELS[m.lastRole] ?? m.lastRole : 'Member'}
                                                </span>
                                            </td>
                                            <td className='px-4 py-3 text-neutral-600'>
                                                {new Date(m.removedAt).toLocaleDateString('en-US', {
                                                    month: 'short',
                                                    day: 'numeric',
                                                    year: 'numeric',
                                                })}
                                                {m.removedBy ? ` by ${m.removedBy}` : ''}
                                            </td>
                                            <td className='px-4 py-3 text-right'>
                                                <Link
                                                    to={`${BASE}/team/former/${m.userId}`}
                                                    className='inline-flex items-center px-3 py-1.5 text-xs font-medium text-neutral-700 bg-neutral-50 hover:bg-neutral-100 border border-neutral-200 rounded-lg transition-colors'
                                                >
                                                    View
                                                </Link>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {showAdd && (
                <AddMemberModal
                    onClose={() => setShowAdd(false)}
                    onAdd={(member) => setMembers((prev) => [...prev, member])}
                />
            )}
        </div>
    )
}

function Stat({ label, value, sub, info }: { label: string; value: string; sub: React.ReactNode; info?: string }) {
    return (
        <div className='bg-white rounded-xl border border-neutral-200 p-4'>
            <div className='text-xs uppercase tracking-wider text-neutral-500 mb-2'>
                {label}
                {info && <InfoTip text={info} />}
            </div>
            <div className='text-2xl font-bold text-neutral-900'>{value}</div>
            <div className='text-xs text-neutral-500 mt-0.5'>{sub}</div>
        </div>
    )
}

// Week-over-week delta line: a percentage (value) or a count (count + unit).
// `goodWhenUp` colors an increase green (good) or red (bad, e.g. at-risk clients).
function WoW({
    value,
    count,
    unit,
    goodWhenUp,
}: {
    value?: number
    count?: number
    unit?: string
    goodWhenUp: boolean
}) {
    const isPct = value !== undefined
    const delta = isPct ? value! : count!
    if (delta === 0) {
        return <span className='text-neutral-500'>No change vs. previous week</span>
    }
    const up = delta > 0
    const color = up === goodWhenUp ? 'text-green-600' : 'text-red-600'
    const magnitude = isPct
        ? `${Math.abs(Math.round(delta * 100))}%`
        : `${Math.abs(delta)} ${unit}${Math.abs(delta) === 1 ? '' : 's'}`
    return (
        <span>
            <span className={`font-medium ${color}`}>
                {magnitude} {up ? '↑' : '↓'}
            </span>
            <span className='text-neutral-500'> vs. previous week</span>
        </span>
    )
}

const ROLE_OPTIONS: { value: string; label: string; description: string }[] = [
    {
        value: 'admin',
        label: 'Admin',
        description: 'Full control. Manages licenses, team members, and caseload assignments. Sees all clients and analytics.',
    },
    {
        value: 'coordinator',
        label: 'Coordinator',
        description: 'Works hands-on with their assigned clients. Manages their licenses, assigns homework, and writes notes.',
    },
    {
        value: 'viewer',
        label: 'Viewer',
        description: 'Read-only. Sees all clients and analytics across the organization but cannot make any changes.',
    },
]

function AddMemberModal({ onClose, onAdd }: { onClose: () => void; onAdd: (member: MockMember) => void }) {
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [role, setRole] = useState('coordinator')
    const [error, setError] = useState<string | null>(null)
    const [done, setDone] = useState<{ createdNewAccount: boolean } | null>(null)

    function handleSubmit() {
        setError(null)
        if (!email.trim()) {
            setError('Enter an email address')
            return
        }
        const newMember: MockMember = {
            _id: `mem_new_${Date.now()}`,
            userId: `u_new_${Date.now()}`,
            name: name.trim() || null,
            email: email.trim(),
            role,
            addedAt: new Date().toISOString(),
            lastSignInAt: null,
            actions30d: 0,
        }
        onAdd(newMember)
        setDone({ createdNewAccount: true })
    }

    return (
        <div className='fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50' onClick={onClose}>
            <div className='bg-white rounded-2xl shadow-xl w-full max-w-md p-6' onClick={(e) => e.stopPropagation()}>
                <div className='flex items-center justify-between mb-4'>
                    <h2 className='text-lg font-semibold text-neutral-900'>Add a team member</h2>
                    <button onClick={onClose} className='p-1 text-neutral-400 hover:text-neutral-700'>
                        <XMarkIcon className='w-5 h-5' />
                    </button>
                </div>

                {done ? (
                    <>
                        <div className='p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800'>
                            {done.createdNewAccount
                                ? `${email} has been added. They'll receive an email with a link to set their password and sign in.`
                                : `${email} has been added and can sign in with their existing password.`}
                        </div>
                        <div className='flex items-center justify-end gap-2 mt-6'>
                            <button
                                onClick={onClose}
                                className='px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg'
                            >
                                Done
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className='space-y-4'>
                            <div>
                                <label className='block text-sm font-medium text-neutral-700 mb-1.5'>Name</label>
                                <input
                                    type='text'
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className='w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500'
                                />
                            </div>
                            <div>
                                <label className='block text-sm font-medium text-neutral-700 mb-1.5'>Work email</label>
                                <input
                                    type='email'
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className='w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500'
                                />
                            </div>
                            <div>
                                <label className='block text-sm font-medium text-neutral-700 mb-1.5'>Role</label>
                                <div className='space-y-2'>
                                    {ROLE_OPTIONS.map((opt) => (
                                        <button
                                            key={opt.value}
                                            type='button'
                                            onClick={() => setRole(opt.value)}
                                            className={`w-full text-left p-3 rounded-lg border transition-colors ${
                                                role === opt.value
                                                    ? 'border-brand-500 bg-brand-50 ring-1 ring-brand-500'
                                                    : 'border-neutral-200 hover:border-neutral-300 bg-white'
                                            }`}
                                        >
                                            <div className='flex items-center gap-2'>
                                                <span
                                                    className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${
                                                        role === opt.value ? 'border-brand-600' : 'border-neutral-300'
                                                    }`}
                                                >
                                                    {role === opt.value && (
                                                        <span className='w-2 h-2 rounded-full bg-brand-600' />
                                                    )}
                                                </span>
                                                <span className='text-sm font-medium text-neutral-900'>{opt.label}</span>
                                            </div>
                                            <p className='text-xs text-neutral-600 mt-1 ml-6'>{opt.description}</p>
                                        </button>
                                    ))}
                                </div>
                                <p className='text-xs text-neutral-500 mt-2'>
                                    Each email address can hold only one role. An email already in use as a team
                                    member or a client account cannot be added.
                                </p>
                            </div>
                        </div>

                        {error && (
                            <div className='mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800'>
                                {error}
                            </div>
                        )}

                        <div className='flex items-center justify-end gap-2 mt-6'>
                            <button
                                onClick={onClose}
                                className='px-4 py-2 text-sm font-medium text-neutral-700 hover:text-neutral-900'
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                className='px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg'
                            >
                                Add member
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}
